import { appInsights } from '@/utils/appInsightsUtil';
import { stringHasValue } from './stringUtil';

type EventProperty = string | number | boolean;

type BaseEventProperties = {
  route?: string;
  applicationId?: string;
  cycleId?: string;
  partnerId?: string;
  programId?: string;
  durationMs?: number;
  result?: 'success' | 'failure';
  reason?: string;
  statusCode?: number;
};

type BusinessEventMap = {
  ApplicationSubmitted: BaseEventProperties & {
    applicationId: string;
  };
  ApplicationApproved: BaseEventProperties & {
    applicationId: string;
  };
  PaymentFailed: BaseEventProperties & {
    reason: string;
    statusCode?: number;
  };
  StandardNameMissing: BaseEventProperties & {
    standardId?: number | string;
  };
};

function normalizeProperties(properties: Record<string, unknown>): Record<string, EventProperty> {
  const normalized: Record<string, EventProperty> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value == null) {
      continue;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      normalized[key] = value;
      continue;
    }

    normalized[key] = JSON.stringify(value);
  }

  return normalized;
}

export function trackBusinessEvent<T extends keyof BusinessEventMap>(
  name: T,
  properties: BusinessEventMap[T]
): void {
  const normalizedProperties = normalizeProperties(properties);
  const { durationMs, ...eventProperties } = normalizedProperties;

  appInsights.trackEvent({
    name,
    properties: eventProperties,
    measurements: typeof durationMs === 'number' ? { durationMs } : undefined,
  });
}

let globalErrorHandlersAttached = false;

function toError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  return new Error(fallbackMessage);
}

function ensureErrorHasStack(error: Error, syntheticFrame: string): Error {
  if (stringHasValue(error.stack)) {
    return error;
  }

  error.stack = `${error.name}: ${error.message}\n    at ${syntheticFrame}`;
  return error;
}

export function registerGlobalTelemetryErrorHandlers(): void {
  if (globalErrorHandlersAttached) {
    return;
  }

  window.addEventListener('error', (event) => {
    const exception = toError(
      event.error,
      stringHasValue(event.message) ? event.message : 'Window error'
    );
    const sourceLocation = stringHasValue(event.filename)
      ? `${event.filename}:${event.lineno}:${event.colno}`
      : 'window.onerror';
    const exceptionWithStack = ensureErrorHasStack(exception, `window.onerror (${sourceLocation})`);

    appInsights.trackException({
      exception: exceptionWithStack,
      properties: {
        source: 'window.error',
        errorName: exceptionWithStack.name,
        hasStack: stringHasValue(exceptionWithStack.stack),
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    const exception = toError(reason, 'Unhandled promise rejection');
    const exceptionWithStack = ensureErrorHasStack(
      exception,
      'window.onunhandledrejection (PromiseRejectionEvent)'
    );

    appInsights.trackException({
      exception: exceptionWithStack,
      properties: {
        source: 'window.unhandledrejection',
        errorName: exceptionWithStack.name,
        hasStack: stringHasValue(exceptionWithStack.stack),
        reasonType: reason == null ? 'unknown' : typeof reason,
      },
    });
  });

  globalErrorHandlersAttached = true;
}
